import { FunctionComponent } from "react";
import { DropsButton, Text } from "shared/components/Spor";
import collapseIcon from "shared/icons/collapse-icon.svg?react";
import expandIcon from "shared/icons/expand-icon.svg?react";
import styled from "styled-components";

const MakeToggleIcon = (item: FunctionComponent) => styled(item)<{
  $inverted?: boolean;
}>`
  min-width: 18px;
  max-width: 18px;
  height: auto;

  transform: rotate(${({ $inverted }) => ($inverted ? "180deg" : "0")});

  // This changes color for dark mode
  & g {
    & line {
      stroke: ${({ theme }) => theme.colorTextMain};
    }
    & g {
      fill: ${({ theme }) => theme.colorTextMain};
    }
  }
`;

const ExpandIcon = MakeToggleIcon(expandIcon);
const CollapseIcon = MakeToggleIcon(collapseIcon);

const StyledToggleButton = styled(DropsButton)<{
  $open: boolean;
  $expand?: boolean;
}>`
  &&& {
    display: ${({ $open }) => $open && "none"};
    padding: 6px ${({ $expand }) => $expand && "12px"};
    margin-bottom: 4px;
  }

  * {
    column-gap: ${({ $expand }) => !$expand && "0px !important"};
    justify-content: center !important;
  }
`;

export interface ButtonProps {
  label?: string;
  open: boolean;
  setOpen: (newState: boolean) => void;
  inverted?: boolean;
  title?: string;
}

interface BaseToggleProps {
  toggleType: "expand" | "collapse";
}

const BaseToggleButton = ({
  label,
  open,
  setOpen,
  inverted = false,
  title,
  toggleType,
  ...rest
}: ButtonProps & BaseToggleProps) => {
  const expand = toggleType === "expand";
  const invert = expand === inverted;
  const ToggleIcon = expand ? ExpandIcon : CollapseIcon;
  return (
    <StyledToggleButton
      $open={open}
      onClick={() => {
        setOpen(!open);
      }}
      $expand={expand}
      title={title}
      variant="ghost"
      size="xs"
      display="flex"
      alignItems="center"
      leftIcon={invert ? undefined : <ToggleIcon $inverted={invert} />}
      rightIcon={invert ? <ToggleIcon $inverted={invert} /> : undefined}
      {...rest}
    >
      {label && (
        <Text variant="xs" fontWeight="400" style={{ lineHeight: 0 }}>
          {label}
        </Text>
      )}
    </StyledToggleButton>
  );
};

BaseToggleButton.defaultProps = {
  inverted: false,
};

export default BaseToggleButton;
